import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { supabase } from '../../lib/supabase';
import { Button, Spinner, Input, Toast } from '../../components/design-system';
import { AdminPage } from './AdminPage';
import type { Affiliate, AffiliateCommission } from '../../types';

interface AffiliateTotals {
  count: number;
  unpaidCents: number;
  paidCents: number;
}

interface FormState {
  name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  commission_percentage: string;
}

const EMPTY_FORM: FormState = { name: '', contact_name: '', contact_email: '', contact_phone: '', commission_percentage: '10' };

function formatEuros(cents: number): string {
  return (cents / 100).toLocaleString('en', { style: 'currency', currency: 'EUR' });
}

/** Short, URL-safe random token — printed on a physical QR code, so it
 *  needs to stay short and never contain characters that need escaping. */
function generateToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(9));
  return btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, '').slice(0, 10);
}

function affiliateUrl(token: string): string {
  return `${window.location.origin}/qrc?token=${token}`;
}

function QrPreview({ token }: { token: string }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void QRCode.toDataURL(affiliateUrl(token), { width: 240, margin: 1 }).then((url) => {
      if (active) setDataUrl(url);
    });
    return () => { active = false; };
  }, [token]);

  if (!dataUrl) return <div className="w-[120px] h-[120px] flex items-center justify-center"><Spinner size="sm" /></div>;

  return (
    <div className="flex flex-col items-center gap-2">
      <img src={dataUrl} alt={`QR code for ${token}`} width={120} height={120} className="rounded-lg border border-app-border bg-white p-1" />
      <a href={dataUrl} download={`sekretly-affiliate-${token}.png`} className="text-[11px] text-gold hover:underline">Download PNG</a>
    </div>
  );
}

export function AdminAffiliates() {
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [totals, setTotals] = useState<Record<string, AffiliateTotals>>({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Affiliate | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  function flashToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(''), 2400);
  }

  async function load() {
    setLoading(true);
    const [{ data: affiliateRows }, { data: commissionRows }] = await Promise.all([
      supabase.from('affiliates').select('*').order('created_at', { ascending: false }),
      supabase.from('affiliate_commissions').select('affiliate_id, commission_cents, paid_out'),
    ]);
    setAffiliates((affiliateRows ?? []) as Affiliate[]);

    const byAffiliate: Record<string, AffiliateTotals> = {};
    for (const row of (commissionRows ?? []) as Pick<AffiliateCommission, 'affiliate_id' | 'commission_cents' | 'paid_out'>[]) {
      const t = byAffiliate[row.affiliate_id] ?? { count: 0, unpaidCents: 0, paidCents: 0 };
      t.count += 1;
      if (row.paid_out) t.paidCents += row.commission_cents;
      else t.unpaidCents += row.commission_cents;
      byAffiliate[row.affiliate_id] = t;
    }
    setTotals(byAffiliate);
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  const grandTotalUnpaid = useMemo(
    () => Object.values(totals).reduce((sum, t) => sum + t.unpaidCents, 0),
    [totals],
  );

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setFormOpen(true);
  }

  function openEdit(affiliate: Affiliate) {
    setEditing(affiliate);
    setForm({
      name: affiliate.name,
      contact_name: affiliate.contact_name ?? '',
      contact_email: affiliate.contact_email ?? '',
      contact_phone: affiliate.contact_phone ?? '',
      commission_percentage: String(affiliate.commission_percentage),
    });
    setFormError('');
    setFormOpen(true);
  }

  async function handleSave() {
    const pct = Number(form.commission_percentage);
    if (!form.name.trim()) { setFormError('Name is required.'); return; }
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) { setFormError('Commission % must be between 0 and 100.'); return; }

    setSaving(true);
    setFormError('');
    const payload = {
      name: form.name.trim(),
      contact_name: form.contact_name.trim() || null,
      contact_email: form.contact_email.trim() || null,
      contact_phone: form.contact_phone.trim() || null,
      commission_percentage: Math.round(pct),
      updated_at: new Date().toISOString(),
    };

    const { error } = editing
      ? await supabase.from('affiliates').update(payload).eq('id', editing.id)
      // token is generated once here and never changes — it's what's
      // actually encoded in a physical, possibly-already-printed QR code.
      : await supabase.from('affiliates').insert({ ...payload, token: generateToken(), enabled: true });

    setSaving(false);
    if (error) { setFormError(error.message); return; }
    setFormOpen(false);
    flashToast(editing ? 'Affiliate updated.' : 'Affiliate created.');
    void load();
  }

  async function toggleEnabled(affiliate: Affiliate) {
    setBusyId(affiliate.id);
    const { error } = await supabase
      .from('affiliates')
      .update({ enabled: !affiliate.enabled, updated_at: new Date().toISOString() })
      .eq('id', affiliate.id);
    setBusyId(null);
    if (error) { flashToast('Could not update this affiliate.'); return; }
    flashToast(affiliate.enabled
      ? 'Disabled — the QR code still works, but no new commissions will be recorded.'
      : 'Enabled — new referrals will now generate commissions.');
    void load();
  }

  async function markPaid(affiliate: Affiliate) {
    const t = totals[affiliate.id];
    if (!t || t.unpaidCents === 0) return;
    if (!window.confirm(`Mark ${formatEuros(t.unpaidCents)} as paid to ${affiliate.name}? This is just record-keeping — send the payment separately.`)) return;
    setBusyId(affiliate.id);
    const { error } = await supabase
      .from('affiliate_commissions')
      .update({ paid_out: true, paid_out_at: new Date().toISOString() })
      .eq('affiliate_id', affiliate.id)
      .eq('paid_out', false);
    setBusyId(null);
    if (error) { flashToast('Could not update payout status.'); return; }
    flashToast('Marked as paid.');
    void load();
  }

  return (
    <AdminPage
      title="Affiliates"
      description="QR-code referral tokens — enable/disable, set commission rates, and track what's owed."
      actions={<Button size="sm" onClick={openCreate}>New affiliate</Button>}
    >
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <span className="text-xs text-app-muted">{affiliates.length} affiliate{affiliates.length === 1 ? '' : 's'}</span>
        <span className="text-xs text-app-fg">
          Total owed: <span className="font-semibold text-gold">{formatEuros(grandTotalUnpaid)}</span>
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : affiliates.length === 0 ? (
        <p className="text-center text-app-muted text-sm py-12">No affiliates yet.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {affiliates.map((affiliate) => {
            const t = totals[affiliate.id] ?? { count: 0, unpaidCents: 0, paidCents: 0 };
            const isBusy = busyId === affiliate.id;
            return (
              <div key={affiliate.id} className="rounded-xl border border-app-border bg-app-card p-4 flex flex-col sm:flex-row gap-4">
                <QrPreview token={affiliate.token} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-app-fg truncate">{affiliate.name}</h3>
                        <span className={`text-[10px] font-medium rounded-full px-2 py-0.5 ${affiliate.enabled ? 'bg-success/10 text-success' : 'bg-app-bg text-app-muted border border-app-border'}`}>
                          {affiliate.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <p className="text-[11px] text-app-muted mt-0.5 font-mono">{affiliateUrl(affiliate.token)}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(affiliate)}>Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => void toggleEnabled(affiliate)} disabled={isBusy}>
                        {isBusy ? <Spinner size="sm" /> : affiliate.enabled ? 'Disable' : 'Enable'}
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-3">
                    <div>
                      <p className="text-app-muted">Commission</p>
                      <p className="text-app-fg font-medium">{affiliate.commission_percentage}%</p>
                    </div>
                    <div>
                      <p className="text-app-muted">Referred trips</p>
                      <p className="text-app-fg font-medium">{t.count}</p>
                    </div>
                    <div>
                      <p className="text-app-muted">Owed</p>
                      <p className="text-gold font-semibold">{formatEuros(t.unpaidCents)}</p>
                    </div>
                    <div>
                      <p className="text-app-muted">Paid out</p>
                      <p className="text-app-fg font-medium">{formatEuros(t.paidCents)}</p>
                    </div>
                  </div>

                  {(affiliate.contact_name || affiliate.contact_email || affiliate.contact_phone) && (
                    <p className="text-[11px] text-app-muted mb-3">
                      {[affiliate.contact_name, affiliate.contact_email, affiliate.contact_phone].filter(Boolean).join(' · ')}
                    </p>
                  )}

                  {t.unpaidCents > 0 && (
                    <Button size="sm" variant="outline" onClick={() => void markPaid(affiliate)} disabled={isBusy}>
                      Mark {formatEuros(t.unpaidCents)} as paid
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setFormOpen(false)}>
          <div className="bg-app-card border border-app-border rounded-2xl shadow-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-app-border">
              <h2 className="text-base font-semibold text-app-fg">{editing ? 'Edit affiliate' : 'New affiliate'}</h2>
              {!editing && <p className="text-xs text-app-muted mt-1">A unique QR token is generated automatically once you save.</p>}
            </div>
            <div className="px-6 py-5 flex flex-col gap-3">
              <Input label="Affiliate name" placeholder="e.g. Hotel Aviz" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={saving} />
              <Input label="Contact name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} disabled={saving} />
              <Input label="Contact email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} disabled={saving} />
              <Input label="Contact phone" type="tel" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} disabled={saving} />
              <Input label="Commission %" type="number" min={0} max={100} value={form.commission_percentage} onChange={(e) => setForm({ ...form, commission_percentage: e.target.value })} disabled={saving} />
              {formError && <p className="text-xs text-crimson">{formError}</p>}
            </div>
            <div className="px-6 py-4 border-t border-app-border flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={() => void handleSave()} disabled={saving}>
                {saving ? <Spinner size="sm" /> : editing ? 'Save changes' : 'Create affiliate'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast} />
    </AdminPage>
  );
}

export default AdminAffiliates;
